import { Paper, Typography, LinearProgress } from '@mui/material';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

const NativeFolderSync = registerPlugin('NativeFolderSync');

// Shared "presign + poll" flow for "Download uncompressed original" buttons
// (EventPage, Moments, and SharePage's single-photo preview download all use
// this; SharePage's batch "download all uncompressed" has its own map-shaped
// variant of the same idea - see ensureUcOriginals/fetchUcOriginal there).
//
// The backend hands back a presigned S3 URL immediately - including for
// Drive-backed originals whose cache object hasn't been populated yet by its
// background relay job (see backend/routes/uc_originals.py). The URL 404s
// until the object lands, then serves normally, so the client polls it
// directly against S3 rather than calling the backend again.
export const UC_POLL_INTERVAL_MS = 3000;
export const UC_POLL_TIMEOUT_MS = 5 * 60 * 1000; // give up on one file after ~5 minutes

/**
 * Poll a presigned URL until it stops 404ing or `timeoutMs` elapses.
 *
 * `onTick({ elapsedMs, timeoutMs })` fires before each attempt (including the
 * first, at elapsedMs=0) so callers can drive a progress indicator.
 */
export async function pollPresignedUrl(url, { intervalMs = UC_POLL_INTERVAL_MS, timeoutMs = UC_POLL_TIMEOUT_MS, onTick } = {}) {
  const start = Date.now();
  for (;;) {
    const elapsedMs = Date.now() - start;
    if (onTick) onTick({ elapsedMs, timeoutMs });
    if (elapsedMs >= timeoutMs) {
      throw new Error('Timed out waiting for the uncompressed original to be ready.');
    }
    const res = await fetch(url);
    if (res.ok) return res;
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
}

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.onload = () => {
      const commaIndex = String(reader.result).indexOf(',');
      resolve(commaIndex >= 0 ? reader.result.slice(commaIndex + 1) : reader.result);
    };
    reader.readAsDataURL(blob);
  });
}

// Android's stock WebView has no download infrastructure at all - blob:
// URLs (the anchor-click trick below, a normal browser download idiom) are a
// known WebView limitation and silently do nothing there, with no error. On
// native, write the blob to a cache file via @capacitor/filesystem first -
// shared by saveBlobNative below (which hands the resulting path to
// NativeFolderSyncPlugin.saveToMediaStore) and by utils/sharePhoto.js (which
// hands the resulting file:// uri straight to @capacitor/share).
export async function writeBlobToNativeCache(blob, filename) {
  const tempPath = `downloads-tmp/${Date.now()}-${filename}`;
  const base64 = await blobToBase64(blob);
  await Filesystem.writeFile({ path: tempPath, directory: Directory.Cache, data: base64, recursive: true });
  const { uri } = await Filesystem.getUri({ path: tempPath, directory: Directory.Cache });
  return {
    uri,
    cleanup: () => Filesystem.deleteFile({ path: tempPath, directory: Directory.Cache }).catch(() => {}),
  };
}

// Hands the cached file to NativeFolderSyncPlugin's saveToMediaStore, which
// lands it in the device's own Photos app (images) or Downloads/Files app
// (anything else, e.g. a "download all" zip) - see that plugin method for
// the MediaStore details. Off-native, completely unchanged (see saveBlob).
async function saveBlobNative(blob, filename) {
  const { uri, cleanup } = await writeBlobToNativeCache(blob, filename);
  const isArchive = /\.zip$/i.test(filename);
  try {
    await NativeFolderSync.saveToMediaStore({
      tempFilePath: uri.replace(/^file:\/\//, ''),
      filename,
      mimeType: blob.type || (isArchive ? 'application/zip' : 'image/jpeg'),
      collection: isArchive ? 'downloads' : 'images',
    });
  } finally {
    cleanup();
  }
}

// Returns a promise so native failures (a broken MediaStore insert, a
// permission error, etc.) propagate to the caller instead of vanishing -
// every caller in this codebase is expected to surface a rejection here as
// a visible error, not swallow it silently (that's exactly what made the
// native download bug invisible before this was a return value).
export function saveBlob(blob, filename) {
  if (Capacitor.isNativePlatform()) {
    return saveBlobNative(blob, filename);
  }
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
  return Promise.resolve();
}

/**
 * Download a same-page-context file by fetching it (following any
 * redirects, e.g. the photo-original endpoint's 302-to-presigned-S3-URL)
 * and saving the resulting blob, rather than `window.open(url, '_blank')`.
 *
 * window.open on a download URL pushes Android Chrome (and the standalone
 * PWA) into a separate browser activity to show its native "Downloading…"
 * sheet; returning from it re-launches the PWA from its root instead of
 * resuming the SPA where the user left off. Fetching the bytes ourselves and
 * triggering the save via a blob: URL keeps everything in the current page.
 */
export async function downloadUrlAsBlob(url, filename) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  await saveBlob(blob, filename);
}

/**
 * Download one uncompressed original end to end: ask the backend for a
 * (possibly not-yet-populated) presigned URL, poll it until it's ready or
 * times out, then save the file.
 *
 * `requestDownload` is `eventApi.requestUcOriginalDownload` /
 * `momentsApi.requestUcOriginalDownload` - resolves to `{ url }`.
 * `onActiveChange(active)` toggles around the whole operation so callers can
 * show/hide a <UcDownloadProgressBar/>; it always fires a final `false`, even
 * on failure, via `finally`.
 */
export async function downloadUcOriginal(requestDownload, eventId, photoId, { filename, onActiveChange } = {}) {
  if (onActiveChange) onActiveChange(true);
  try {
    const { url } = await requestDownload(eventId, photoId);
    if (!url) throw new Error('No download URL was returned.');
    const res = await pollPresignedUrl(url);
    const blob = await res.blob();
    await saveBlob(blob, filename || `${photoId}-uncompressed.jpg`);
  } finally {
    if (onActiveChange) onActiveChange(false);
  }
}

// Small floating "preparing download" indicator, styled to match SharePage's
// bulk-download progress card (top-right, same palette/sizing) - just without
// a determinate fraction, since there's no way to know how close a Drive
// relay is to finishing.
export function UcDownloadProgressBar({ active, label = 'Preparing uncompressed original…' }) {
  if (!active) return null;
  return (
    <Paper
      elevation={6}
      sx={{
        // Above MUI's 'modal' tier (1300, what the photo-preview Dialog
        // uses) so this stays visible over it instead of being covered by
        // the Dialog's own portal - matches the 'snackbar' tier, since a
        // transient overlay notification should outrank a modal it can
        // legitimately appear alongside.
        position: 'fixed', top: 16, right: 16, zIndex: 1400,
        width: 260, p: 1.5, borderRadius: 2,
        border: '1px solid', borderColor: 'divider',
      }}
    >
      <Typography variant="caption" fontWeight={700} color="#C73866" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <LinearProgress
        variant="indeterminate"
        sx={{ borderRadius: 1, height: 6, mb: 0.5, '& .MuiLinearProgress-bar': { bgcolor: '#C73866' } }}
      />
      <Typography variant="caption" color="text.secondary">
        This can take a moment for large files…
      </Typography>
    </Paper>
  );
}
